/* eslint-disable @typescript-eslint/no-use-before-define */
import { useAngularContext } from 'AngularContext';
import { useCurrentUser, useOfflineModeManager } from 'FrontRoyalAngular';
import { type Stream, streamDashboardPath } from 'Lessons';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStreams } from 'Streams';
import { type RouteAnimationHelper as RouteAnimationHelperClass } from 'RouteAnimationHelper';
import './locales/bookmarkedCourses/bookmarkedCourses-en.json';
import { type ProgressStatus, useContentProgress } from 'ContentProgress';
import { getStreamProgressStatus } from 'ContentProgress/utils/getProgressStatus';
import { CourseList } from './CourseList';

export const BookmarkedAndCompletedCourses = memo(() => {
    const RouteAnimationHelper = useAngularContext().get<RouteAnimationHelperClass>('RouteAnimationHelper');
    const { streamProgress } = useContentProgress();
    const currentUser = useCurrentUser();
    const favoriteStreamLocalePackIds = useMemo(
        () => currentUser?.favoriteLessonStreamLocalePacks?.map(lp => lp.id) ?? [],
        [currentUser],
    );
    const completedStreamLocalePackIds = useMemo(
        () => streamProgress.filter(record => !!record.completedAt).map(record => record.localePackId),
        [streamProgress],
    );

    const completedCourses = useStreams(completedStreamLocalePackIds);
    const allBookmarkedCourses = useStreams(favoriteStreamLocalePackIds);

    const bookmarkedCourses = useMemo(
        () => allBookmarkedCourses.filter(course => !completedStreamLocalePackIds.includes(course.localePack.id)),
        [allBookmarkedCourses, completedStreamLocalePackIds],
    );

    const getProgressInfo = useCallback(
        (stream: Stream) => {
            const progress = streamProgress.find(sp => sp.localePackId === stream.localePack.id);

            return {
                status: getStreamProgressStatus(progress),
            };
        },
        [streamProgress],
    );

    const onPress = useCallback(
        (course: Stream) => {
            RouteAnimationHelper.animatePathChange(
                streamDashboardPath({ streamTitle: course.title, streamId: course.id }),
                'slide-left',
            );
        },
        [RouteAnimationHelper],
    );

    return (
        <BookmarkedAndCompletedCoursesPresentation
            bookmarkedCourses={bookmarkedCourses}
            completedCourses={completedCourses}
            onPress={onPress}
            getProgressInfo={getProgressInfo}
        />
    );
});

export const BookmarkedAndCompletedCoursesPresentation = memo(
    ({
        bookmarkedCourses,
        completedCourses,
        onPress,
        getProgressInfo,
    }: {
        bookmarkedCourses: Stream[];
        completedCourses: Stream[];
        onPress: (course: Stream) => void;
        getProgressInfo: (stream: Stream) => { status: ProgressStatus } | null;
    }) => {
        const { t } = useTranslation('back_royal', { keyPrefix: 'bookmarkedCourses.bookmarkedCourses' });
        const OfflineModeManager = useOfflineModeManager();

        if (OfflineModeManager.inOfflineMode) return null;

        return (
            <div className="flex flex-col gap-10">
                <CourseList
                    courses={bookmarkedCourses}
                    header={t('bookmarkedCourses')}
                    onPress={onPress}
                    getProgressInfo={getProgressInfo}
                    initiallyLimitShownCourses
                />
                <CourseList
                    courses={completedCourses}
                    header={t('completedCourses')}
                    onPress={onPress}
                    getProgressInfo={getProgressInfo}
                    initiallyLimitShownCourses
                />
            </div>
        );
    },
);
